import React, { useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../services/services/APIs';
import { clearSmartnetTokenStorage, setSmartnetTokenStorage } from '../services/api/configurationAPIs';
import { clearDashboardCache } from '../utils/dashboardCache';
import {
  APP_BUILD_ID_KEY,
  clearClientStorageAndReload,
  getCurrentBuildId,
  shouldRecoverStaleClient,
} from '../utils/staleClientRecovery';
import { AuthContext } from './auth-context';

const USER_SESSION_KEY = 'smartnet_user_session_v1';

const buildUserFromSession = (session) => {
  const cuenta = session?.cuentaModel;
  if (!cuenta) return null;
  return {
    ...cuenta,
    PermisosModel: Array.isArray(session?.PermisosModel) ? session.PermisosModel : [],
    ElementosNavegacionModel: Array.isArray(session?.ElementosNavegacionModel)
      ? session.ElementosNavegacionModel
      : [],
  };
};

const persistUserSession = (session) => {
  const built = buildUserFromSession(session);
  if (!built || typeof sessionStorage === 'undefined') return built;
  try {
    sessionStorage.setItem(USER_SESSION_KEY, JSON.stringify(session));
  } catch {
    /* silent */
  }
  return built;
};

const restoreUserFromStorage = () => {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(USER_SESSION_KEY);
    if (!raw) return null;
    return buildUserFromSession(JSON.parse(raw));
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const resetSessionState = useCallback(() => {
    clearSmartnetTokenStorage();
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(USER_SESSION_KEY);
    }
    if (typeof localStorage !== 'undefined') {
      const buildId = localStorage.getItem(APP_BUILD_ID_KEY);
      localStorage.clear();
      if (buildId) {
        try {
          localStorage.setItem(APP_BUILD_ID_KEY, buildId);
        } catch {
          /* silent */
        }
      }
    }
    void clearDashboardCache();
    setUser(null);
  }, []);

  useEffect(() => {
    setUser(restoreUserFromStorage());
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    resetSessionState();
    try {
      const loginRes = await api.auth.login(username, password);
      let loginData = {};
      try {
        loginData = await loginRes.json();
      } catch (parseError) {
        if (shouldRecoverStaleClient(parseError)) {
          await clearClientStorageAndReload();
          return { ok: false, message: 'Actualizando aplicación...' };
        }
        throw parseError;
      }

      if (!loginRes.ok) {
        return { ok: false, message: loginData.message || 'Usuario o contraseña inválidos' };
      }

      const tokenFromLogin =
        loginData.token ||
        loginData.accessToken ||
        loginData.data?.token ||
        loginData.data?.accessToken;
      if (typeof tokenFromLogin === 'string' && tokenFromLogin) {
        setSmartnetTokenStorage(tokenFromLogin);
      }

      if (loginData.success && loginData.session?.cuentaModel) {
        try {
          localStorage.setItem(APP_BUILD_ID_KEY, getCurrentBuildId());
        } catch {
          /* silent */
        }
        setUser(persistUserSession(loginData.session));
        return { ok: true };
      }

      if (loginRes.ok && loginData.success) {
        await clearClientStorageAndReload();
        return { ok: false, message: 'Actualizando aplicación...' };
      }

      resetSessionState();
      return {
        ok: false,
        message: loginData.message || 'No se pudo iniciar sesión',
      };
    } catch (error) {
      if (shouldRecoverStaleClient(error)) {
        await clearClientStorageAndReload();
        return { ok: false, message: 'Actualizando aplicación...' };
      }
      resetSessionState();
      return {
        ok: false,
        message: 'Error de red al iniciar sesión. Intenta nuevamente.',
      };
    }
  };

  const logout = async () => {
    try {
      await api.auth.logout();
    } finally {
      resetSessionState();
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return ctx;
};
